import i18n from "i18next";
import * as z from "zod";

type NumberBounds = {
  default: number;
  exclusiveMinimum: number;
  exclusiveMaximum: number;
};

/**
 * A coerced number that must be strictly within its exclusive bounds, with
 * localized validation messages. The bounds come from the rule preset config
 * where `exclusiveMinimum`/`exclusiveMaximum` mean the boundary itself is not
 * allowed, so we use `.gt()`/`.lt()` rather than `.min()`/`.max()`. Messages are
 * resolved lazily at parse time so they follow the currently selected language.
 */
export const boundedNumberSchema = ({
  default: defaultValue,
  exclusiveMinimum,
  exclusiveMaximum,
}: NumberBounds) =>
  z.coerce
    .number()
    .gt(exclusiveMinimum, {
      error: () =>
        i18n.t("error_value_must_be_greater_than", {
          value: String(exclusiveMinimum),
        }),
    })
    .lt(exclusiveMaximum, {
      error: () =>
        i18n.t("error_value_must_be_less_than", {
          value: String(exclusiveMaximum),
        }),
    })
    .default(defaultValue);

export const configurationSchema = z
  .object({
    execute_actions_on_alert_to_ok: z.boolean().default(true),
  })
  .default({ execute_actions_on_alert_to_ok: true });

export const timeRestrictionSchema = z
  .object({
    start: z.string().default("00:00:00"),
    end: z.string().default("23:59:00"),
  })
  .nullable()
  .default(null);

export type TimeRestrictionSchema = z.infer<typeof timeRestrictionSchema>;
